import { useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { COUNTRIES } from '@astrologermatch/shared';
import { LocationAutocomplete, type ResolvedPlace } from '../components/LocationAutocomplete.js';
import { SITE_NAME } from '../components/Layout.js';
import { Turnstile } from '../components/Turnstile.js';
import { api, ApiError } from '../lib/api.js';
import { useSeo } from '../lib/seo.js';
import { useSession } from '../lib/session.js';
import { useSettings } from '../lib/state.js';
import { useAdoptEarlyInput } from '../lib/earlyInput.js';
import { useHydrated } from '../lib/hydrated.js';

// Ported from prototype/src/pages/AstrologerAuth.jsx. Prototype-only pieces (demo login box,
// on-screen verification link) are replaced by the real email flow (brief §2).

interface RegisterForm {
  name: string;
  email: string;
  phone: string;
  country: string;
  city: string;
  password: string;
  confirm: string;
  agree: boolean;
}

interface LoginResponse {
  astrologerId: string;
  name: string;
  csrfToken: string;
}

export function AstrologerAuth() {
  const settings = useSettings();
  const hydrated = useHydrated();
  const navigate = useNavigate();
  const { setSession } = useSession();
  const [tab, setTab] = useState<'register' | 'login' | 'forgot'>('register');
  const [err, setErr] = useState<ReactNode>('');
  const [info, setInfo] = useState('');
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState<RegisterForm>({
    name: '',
    email: '',
    phone: '',
    country: 'India',
    city: '',
    password: '',
    confirm: '',
    agree: false,
  });
  const [place, setPlace] = useState<ResolvedPlace | null>(null);
  const [login, setLogin] = useState({ email: '', password: '' });
  const loginEmailRef = useRef<HTMLInputElement>(null);
  const loginPasswordRef = useRef<HTMLInputElement>(null);
  useAdoptEarlyInput(loginEmailRef, (email) => {
    setLogin((l) => ({ ...l, email }));
  });
  useAdoptEarlyInput(loginPasswordRef, (password) => {
    setLogin((l) => ({ ...l, password }));
  });
  // The register tab is the one the server renders first, so it's the one a person on a slow
  // connection can start typing into before hydration (LANE-D-31 — the fix for the chat, the
  // review form and the login fields didn't reach this one).
  const nameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  useAdoptEarlyInput(nameRef, (name) => {
    setF((v) => ({ ...v, name }));
  });
  useAdoptEarlyInput(emailRef, (email) => {
    setF((v) => ({ ...v, email }));
  });
  const [forgotEmail, setForgotEmail] = useState('');
  const [sent, setSent] = useState<string | null>(null); // email a verification link went to
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);

  useSeo({
    title: `List Your Astrology Practice – Free Website SEO Audit | ${SITE_NAME}`,
    description:
      'Astrologers: get recommended by our AI to clients worldwide. Free listing, verified badge, client reviews and a free SEO audit of your website.',
    canonical: `${settings.siteUrl}/astrologer`,
  });

  const set = (k: keyof RegisterForm) => (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value =
      e.target instanceof HTMLInputElement && e.target.type === 'checkbox'
        ? e.target.checked
        : e.target.value;
    setF({ ...f, [k]: value });
  };

  function switchTab(t: typeof tab) {
    setTab(t);
    setErr('');
    setInfo('');
  }

  async function doRegister(e?: FormEvent) {
    e?.preventDefault();
    setErr('');
    if (!f.name.trim()) {
      setErr('Please enter your name.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) {
      setErr('Please enter a valid email address.');
      return;
    }
    if (f.phone.trim().length < 6) {
      setErr('Please enter your mobile / WhatsApp number.');
      return;
    }
    if (!f.city.trim()) {
      setErr('Please enter your city.');
      return;
    }
    if (f.password.length < 10) {
      setErr('Password must be at least 10 characters.');
      return;
    }
    if (f.password !== f.confirm) {
      setErr('Passwords do not match.');
      return;
    }
    if (!f.agree) {
      setErr('Please accept the terms to continue.');
      return;
    }
    if (settings.turnstileSiteKey && !turnstileToken) {
      setErr('Please complete the check above the button.');
      return;
    }
    setBusy(true);
    try {
      const res = await api<{ email: string }>('POST', '/api/v1/astrologers/register', {
        name: f.name.trim(),
        loginEmail: f.email.trim(),
        phone: f.phone.trim(),
        country: f.country,
        city: f.city.trim(),
        ...(place
          ? {
              locationPlaceId: place.placeId,
              locationFormattedAddr: place.formattedAddress,
              locationState: place.state,
              locationCountryCode: place.countryCode,
              locationLat: place.lat,
              locationLng: place.lng,
            }
          : {}),
        password: f.password,
        confirmPassword: f.confirm,
        agreeToTerms: true,
        ...(turnstileToken ? { turnstileToken } : {}),
      });
      setSent(res.email);
    } catch (error) {
      setErr(error instanceof ApiError ? error.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  // Only the latest outcome is shown: a success message never sits next to "Too many attempts"
  // from a later click (LANE-D-23).
  async function resend(email: string) {
    setErr('');
    setInfo('');
    try {
      await api('POST', '/api/v1/verify-email/resend', { loginEmail: email });
      setSent(email);
      setInfo('A new link is on its way.');
    } catch (error) {
      setInfo('');
      setErr(error instanceof ApiError ? error.message : 'Could not resend the link.');
    }
  }

  async function doLogin(e: FormEvent) {
    e.preventDefault();
    setErr('');
    // Say which field is missing instead of a generic "Invalid input" (LANE-D-29).
    if (!login.email.trim()) {
      setErr('Please enter your email.');
      loginEmailRef.current?.focus();
      return;
    }
    if (!login.password) {
      setErr('Please enter your password.');
      loginPasswordRef.current?.focus();
      return;
    }
    setBusy(true);
    try {
      const res = await api<LoginResponse>('POST', '/api/v1/astrologers/login', {
        loginEmail: login.email.trim(),
        password: login.password,
      });
      setSession({ role: 'astrologer', name: res.name, csrfToken: res.csrfToken });
      void navigate('/dashboard');
    } catch (error) {
      if (error instanceof ApiError && error.code === 'EMAIL_NOT_VERIFIED') {
        const email = login.email.trim().toLowerCase();
        setErr(
          <>
            Please verify your email first — we sent a link to <b>{email}</b>.{' '}
            <button type="button" className="linkish" onClick={() => void resend(email)}>
              Resend the link
            </button>
          </>,
        );
      } else {
        setErr(
          error instanceof ApiError ? error.message : 'Something went wrong. Please try again.',
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function doForgot(e: FormEvent) {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      await api('POST', '/api/v1/password-reset/request', { loginEmail: forgotEmail.trim() });
      setInfo(
        'If an account exists for that email, we have sent a password reset link. It works for 1 hour.',
      );
    } catch (error) {
      setErr(error instanceof ApiError ? error.message : 'Something went wrong. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="wrap auth-grid">
        <section className="auth-pitch">
          <div className="eyebrow">For astrologers</div>
          <h1>Get recommended to clients who need exactly your expertise</h1>
          <p className="lede-dark">
            Clients describe their problem to our AI. It checks your specialities, website and
            client ratings, and recommends you when you&apos;re the right fit.
          </p>
          <ul className="ticks">
            <li>
              <b>Free SEO audit of your website</b> — a score out of 100 and a clear list of what to
              fix
            </li>
            <li>
              <b>Reach clients worldwide</b> — in your city, your country, or anywhere for online
              consultations
            </li>
            <li>
              <b>Matched on your specialities</b> — marriage, career, vastu, tarot and more
            </li>
            <li>
              <b>Clients contact you directly</b> — by call, WhatsApp or your website, with no
              commission on your consultations
            </li>
            <li>
              <b>Verified badge</b> — shows clients you&apos;re a genuine, established practice
            </li>
          </ul>
        </section>

        {sent ? (
          <section className="card auth-card center">
            <div className="big-check mail" aria-hidden="true">
              ✉
            </div>
            <h2>Check your inbox</h2>
            <p className="muted">
              We&apos;ve sent a verification link to <b>{sent}</b>. Click it to activate your
              account, then log in. The link expires in 24 hours.
            </p>
            {info && (
              <div className="alert alert-info" role="status">
                {info}
              </div>
            )}
            {err && (
              <div className="alert alert-error" role="alert">
                {err}
              </div>
            )}
            <div className="row-btns mt16 justify-center">
              <button type="button" className="btn btn-ghost" onClick={() => void resend(sent)}>
                Resend link
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  setSent(null);
                  switchTab('login');
                }}
              >
                Go to log in
              </button>
            </div>
            <p className="tiny muted mt16">
              Didn&apos;t get it? Check spam, or make sure {sent} is correct.
            </p>
          </section>
        ) : (
          <section className="card auth-card">
            <div className="tabs" role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'register'}
                aria-disabled={!hydrated}
                className={tab === 'register' ? 'active' : ''}
                onClick={() => {
                  switchTab('register');
                }}
              >
                Create account
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab !== 'register'}
                aria-disabled={!hydrated}
                className={tab !== 'register' ? 'active' : ''}
                onClick={() => {
                  switchTab('login');
                }}
              >
                Log in
              </button>
            </div>

            {err && (
              <div className="alert alert-error" role="alert">
                {err}
              </div>
            )}
            {info && (
              <div className="alert alert-info" role="status">
                {info}
              </div>
            )}

            {tab === 'register' && (
              <form onSubmit={(e) => void doRegister(e)} className="form" noValidate>
                <label>
                  Full name
                  <input
                    ref={nameRef}
                    value={f.name}
                    onChange={set('name')}
                    autoComplete="name"
                    maxLength={191}
                    placeholder="e.g. Pandit Ravi Sharma"
                  />
                </label>
                <label>
                  Email
                  <input
                    ref={emailRef}
                    type="email"
                    value={f.email}
                    onChange={set('email')}
                    autoComplete="email"
                    maxLength={191}
                    placeholder="you@gmail.com is fine"
                  />
                </label>
                <div className="row2">
                  <label>
                    Mobile / WhatsApp
                    <input
                      type="tel"
                      value={f.phone}
                      onChange={set('phone')}
                      autoComplete="tel"
                      maxLength={32}
                      placeholder="+91 98xxx xxxxx"
                    />
                  </label>
                  <label>
                    Country
                    <select value={f.country} onChange={set('country')}>
                      {COUNTRIES.map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                  </label>
                </div>
                <LocationAutocomplete
                  label="City"
                  value={f.city}
                  placeholder="e.g. Pune"
                  onChange={(city) => {
                    setF((v) => ({ ...v, city }));
                  }}
                  onResolve={(p) => {
                    setPlace(p);
                    if (p) setF((v) => ({ ...v, city: p.city, country: p.country || v.country }));
                  }}
                />
                <div className="row2">
                  <label>
                    Password
                    <input
                      type="password"
                      value={f.password}
                      onChange={set('password')}
                      autoComplete="new-password"
                      placeholder="At least 10 characters"
                    />
                  </label>
                  <label>
                    Confirm password
                    <input
                      type="password"
                      value={f.confirm}
                      onChange={set('confirm')}
                      autoComplete="new-password"
                    />
                  </label>
                </div>
                <label className="check">
                  <input type="checkbox" checked={f.agree} onChange={set('agree')} /> I agree to the{' '}
                  Terms and the Advertiser Policy, and confirm I won&apos;t promise guaranteed
                  outcomes to clients.
                </label>
                <Turnstile siteKey={settings.turnstileSiteKey} onToken={setTurnstileToken} />
                {/* type="button" (LANE-D-32): a native submit button, tapped before the page's
                    JavaScript has taken over, reloads the page and loses everything typed. This
                    only ever submits through the click handler below, once React is listening. */}
                <button
                  type="button"
                  className="btn btn-primary btn-block"
                  disabled={busy}
                  onClick={() => void doRegister()}
                >
                  {busy ? 'Creating account…' : 'Create account'}
                </button>
                <p className="tiny muted center">
                  We&apos;ll email a verification link to this address.
                </p>
              </form>
            )}

            {tab === 'login' && (
              <form onSubmit={(e) => void doLogin(e)} className="form" noValidate>
                <label>
                  Email
                  <input
                    ref={loginEmailRef}
                    type="email"
                    value={login.email}
                    autoComplete="email"
                    onChange={(e) => {
                      setLogin({ ...login, email: e.target.value });
                    }}
                  />
                </label>
                <label>
                  Password
                  <input
                    ref={loginPasswordRef}
                    type="password"
                    value={login.password}
                    autoComplete="current-password"
                    onChange={(e) => {
                      setLogin({ ...login, password: e.target.value });
                    }}
                  />
                </label>
                <button className="btn btn-primary btn-block" disabled={busy}>
                  {busy ? 'Logging in…' : 'Log in'}
                </button>
                <div className="split small">
                  <button
                    type="button"
                    className="linkish"
                    onClick={() => {
                      setForgotEmail(login.email);
                      switchTab('forgot');
                    }}
                  >
                    Forgot password?
                  </button>
                </div>
              </form>
            )}

            {tab === 'forgot' && (
              <form onSubmit={(e) => void doForgot(e)} className="form" noValidate>
                <p className="small muted">
                  Enter your login email and we&apos;ll send you a link to choose a new password.
                </p>
                <label>
                  Email
                  <input
                    type="email"
                    value={forgotEmail}
                    autoComplete="email"
                    onChange={(e) => {
                      setForgotEmail(e.target.value);
                    }}
                  />
                </label>
                <button className="btn btn-primary btn-block" disabled={busy}>
                  Send reset link
                </button>
                <button
                  type="button"
                  className="linkish small"
                  onClick={() => {
                    switchTab('login');
                  }}
                >
                  Back to log in
                </button>
              </form>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
